import { AlertTriangle } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCheck } from "react-icons/fa6";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "../lib/utils";
import { Step } from "../types";
import Modal from "./Modal";
import { Button } from "./ui/button";

export default function Stepsbar({
  steps,
  goToStep,
  isPending,
}: {
  steps: Step[];
  goToStep?: (step: number) => void;
  isPending?: boolean;
}) {
  const { t } = useTranslation();

  const [showAlert, setShowAlert] = useState(false);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const getValidStepIndex = (steps: Step[], currentIndex: number) => {
    return steps
      .slice(0, currentIndex + 1)
      .filter((step) => step.number !== null).length;
  };

  return (
    <div
      className={`z-40 lg:relative sticky lg:top-auto top-3 backdrop-blur-md lg:ml-0 -ml-3  flex lg:flex-row flex-col lg:flex-1 justify-center items-center lg:gap-0 gap-1.5 transition-colors duration-300 ${pathname.startsWith("/registrasi") ? "h-auto" : "lg:h-auto h-screen"}`}
    >
      {steps.map((step, index) => {
        if (step.text === null) {
          return (
            <div
              key={index}
              className={cn(
                `lg:mr-3 mb-2 lg:mb-5 flex-1 h-1 lg:w-auto w-0.5 rounded-full transition-colors duration-300 ${
                  step.status === "active"
                    ? "bg-primary text-white dark:text-slate-900"
                    : step.status === "completed"
                      ? "bg-secondary-foreground text-white dark:bg-slate-200 dark:text-slate-900"
                      : "bg-secondary dark:bg-slate-700"
                }`,
              )}
            />
          );
        }

        return (
          <div key={index}>
            <div className="flex justify-center items-center gap-1.5 lg:gap-3">
              <div
                className={cn(
                  `lg:w-10 w-6 lg:h-10 h-6 lg:text-base text-xs rounded-full flex justify-center items-center transition-all duration-300 ${
                    step.status === "active" && step.tabStatus === "active"
                      ? "bg-primary text-white dark:text-slate-900"
                      : step.status === "completed"
                        ? "bg-secondary-foreground text-white dark:bg-slate-200 dark:text-slate-900"
                        : "bg-secondary dark:bg-slate-800 dark:text-slate-400"
                  } ${
                    step.tabStatus === "active"
                      ? "bg-primary dark:bg-primary"
                      : ""
                  } ${
                    goToStep
                      ? "cursor-pointer hover:border hover:border-primary dark:hover:border-blue-400 duration-300"
                      : ""
                  }`,
                )}
                onClick={() => {
                  const step = steps[index];
                  const prevSteps = steps.slice(0, index);

                  const allPrevStepsCompleted = prevSteps.every(
                    (prevStep) => prevStep.status === "completed",
                  );

                  const isStepCompleted = step.status === "completed";

                  if (
                    step.number !== null &&
                    (allPrevStepsCompleted || isStepCompleted) &&
                    goToStep
                  ) {
                    const validStepNumber = getValidStepIndex(steps, index);
                    goToStep(validStepNumber);
                    const currentUrl = window.location.search;
                    const urlSearchParams = new URLSearchParams(currentUrl);
                    urlSearchParams.set("step", String(validStepNumber));
                    navigate(`?${urlSearchParams.toString()}`);
                  } else {
                    if (isPending) return;

                    const incompleteStep = prevSteps.find(
                      (prevStep) => prevStep.status !== "completed",
                    );
                    const incompleteStepText = incompleteStep
                      ? incompleteStep.text
                      : `${t("siswa.layout.step.description.sebelumnya")}`;

                    // toast.error(
                    //   `Data ${incompleteStepText} ${t(
                    //     "siswa.layout.step.description.warning",
                    //   )}`,
                    // );
                    setMessage(
                      `Data ${incompleteStepText} ${t(
                        "siswa.layout.step.description.warning",
                      )}`,
                    );
                    setShowAlert(true);
                    setTimeout(() => {
                      setShowAlert(false);
                    }, 20000);
                  }
                }}
              >
                {step.status === "completed" ? (
                  <FaCheck className="w-3 h-3 lg:w-4 lg:h-4" />
                ) : (
                  step.number
                )}
              </div>
            </div>
            <h1 className="mt-1 w-fit mx-auto font-semibold lg:text-sm text-[10px] leading-tight text-center lg:max-w-none max-w-[60px] text-slate-900 dark:text-slate-200 transition-colors duration-300">
              {step.text}
            </h1>
          </div>
        );
      })}

      <Modal
        show={showAlert}
        onClose={() => setShowAlert(false)}
        className="w-[90vw] max-w-md p-0 overflow-hidden bg-white dark:bg-slate-950"
      >
        <div className="p-6 sm:p-8 flex flex-col items-center text-center">
          {/* 1. Icon Container */}
          <div className="relative flex items-center justify-center w-16 h-16 mb-5 rounded-full bg-amber-100 dark:bg-amber-900/30">
            {/* Aksen glow di belakang ikon (opsional untuk efek premium) */}
            <div
              className="absolute inset-0 rounded-full bg-amber-200/50 dark:bg-amber-800/20 animate-ping"
              style={{ animationDuration: "3s" }}
            ></div>
            <AlertTriangle className="w-8 h-8 text-amber-600 dark:text-amber-500 relative z-10" />
          </div>

          {/* 2. Title */}
          <h2 className="mb-2 text-xl font-bold text-slate-900 dark:text-white">
            {t("siswa.layout.step.title.warning")}
          </h2>

          {/* 3. Message Box */}
          <div className="w-full p-4 mt-2 text-left rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900/50 shadow-sm">
            <p className="text-sm text-amber-800 dark:text-amber-200/90 leading-relaxed">
              {message}
            </p>
          </div>

          {/* 4. Action Button */}
          <div className="w-full mt-8">
            <Button
              onClick={() => setShowAlert(false)}
              className="w-full py-6 text-base font-semibold shadow-sm transition-all"
            >
              {t("siswa.layout.button.close") || "Mengerti"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
